// Markdown <-> @lexical/table. Split out of transformers.ts to keep that file readable.

import {$convertToMarkdownString, ElementTransformer, Transformer} from '@lexical/markdown'
import {
    $createTableCellNode,
    $createTableNode,
    $createTableRowNode,
    $isTableCellNode,
    $isTableNode,
    $isTableRowNode,
    TableCellHeaderStates,
    TableCellNode,
    TableNode,
} from '@lexical/table'
import {$createParagraphNode, $createTextNode, $isParagraphNode, $isTextNode, LexicalNode} from 'lexical'

const debug = false
// const debug = "TABLE_TRANSFORMER"

const TABLE_ROW_REG_EXP         = /^(?:\|)(.+)(?:\|)\s?$/
const TABLE_ROW_DIVIDER_REG_EXP = /^(\| ?:?-*:? ?)+\|\s?$/

const createCell = (textContent: string, isHeader = false) => {
    const cell      = $createTableCellNode(isHeader ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS)
    const paragraph = $createParagraphNode()

    const text = textContent.replace(/\\n/g, '\n').trim()
    if (text !== '') {
        paragraph.append($createTextNode(text))
    }

    cell.append(paragraph)
    return cell
}

const mapToCells = (textContent: string): string[] | null => {
    const match = textContent.match(TABLE_ROW_REG_EXP)
    if (!match || !match[1]) {
        return null
    }

    return match[1].split('|')
}

const getColumnCount = (table: TableNode) => {
    const firstRow = table.getFirstChild()

    return $isTableRowNode(firstRow) ? firstRow.getChildrenSize() : 0
}

// Marks every cell in a row as a row header
const markRowAsHeader = (row: LexicalNode) => {
    if (!$isTableRowNode(row)) {
        return
    }

    _.forEach(row.getChildren(), cell => {
        if ($isTableCellNode(cell)) {
            cell.setHeaderStyles(TableCellHeaderStates.ROW, TableCellHeaderStates.ROW)
        }
    })
}

// getCellTransformers is a getter, not a list: the full transformer set lives in transformers.ts,
// which imports this module, so it cannot be read at module-init time.
export const createTableTransformer = (getCellTransformers: () => Array<Transformer>): ElementTransformer => ({
    dependencies: [TableNode],

    export: (node: LexicalNode) => {
        if (!$isTableNode(node)) {
            return null
        }

        const output: string[] = []

        _.forEach(node.getChildren(), row => {
            if (!$isTableRowNode(row)) {
                return
            }

            const cells: string[] = []
            let isHeaderRow = false

            _.forEach(row.getChildren(), cell => {
                if (!$isTableCellNode(cell)) {
                    return
                }

                // the caller supplies the set without TABLE in it - cells only hold blocks, and
                // including TABLE risks recursing back into this exporter
                cells.push($convertToMarkdownString(getCellTransformers(), cell as TableCellNode)
                    .replace(/\n/g, '\\n')
                    .trim())

                if ((cell as TableCellNode).hasHeaderState(TableCellHeaderStates.ROW)) {
                    isHeaderRow = true
                }
            })

            output.push('| {0} |'.f(cells.join(' | ')))

            if (isHeaderRow) {
                output.push('| {0} |'.f(_.map(cells, () => '---').join(' | ')))
            }
        })

        debug && console.log(debug, 'export', output)

        return output.join('\n')
    },

    regExp: TABLE_ROW_REG_EXP,

    replace: (parentNode, _children, match) => {
        // A divider row promotes the preceding table's last row to headers rather than adding a row
        if (TABLE_ROW_DIVIDER_REG_EXP.test(match[0])) {
            const table = parentNode.getPreviousSibling()
            if (!$isTableNode(table)) {
                return
            }

            markRowAsHeader(table.getLastChild() as LexicalNode)
            parentNode.remove()
            return
        }

        const matchCells = mapToCells(match[0])
        if (matchCells == null) {
            return
        }

        // Walk backwards over preceding single-text paragraphs, collecting earlier rows
        const rows      = [matchCells]
        let sibling     = parentNode.getPreviousSibling()
        let maxCells    = matchCells.length

        while (sibling) {
            if (!$isParagraphNode(sibling) || sibling.getChildrenSize() !== 1) {
                break
            }

            const firstChild = sibling.getFirstChild()
            if (!$isTextNode(firstChild)) {
                break
            }

            const cells = mapToCells(firstChild.getTextContent())
            if (cells == null) {
                break
            }

            maxCells = Math.max(maxCells, cells.length)
            rows.unshift(cells)

            const previousSibling = sibling.getPreviousSibling()
            sibling.remove()
            sibling = previousSibling
        }

        const table = $createTableNode()
        _.forEach(rows, cells => {
            const row = $createTableRowNode()
            // rows must be rectangular - pad short ones
            for (let i = 0; i < maxCells; i++) {
                row.append(createCell(i < cells.length ? cells[i] : ''))
            }
            table.append(row)
        })

        // Consecutive markdown row lines build one table rather than many
        const previousSibling = parentNode.getPreviousSibling()
        if ($isTableNode(previousSibling) && getColumnCount(previousSibling) === maxCells) {
            _.forEach(table.getChildren(), row => {
                previousSibling.append(row)
            })
            parentNode.remove()
            return
        }

        parentNode.replace(table)
    },

    type: 'element',
})
